import type { DisplayColumn } from 'types/dataHelios';

/**
 * Extracts and formats the value from a record column for display.
 * Handles arrays, objects, and primitive values.
 */
export const getDisplayColumnValue = (
  record: { items: Array<{ columnKey: string; formattedValue?: unknown; rawValue?: unknown }> },
  columns: Array<{ itemId: number; columnKey: string; label: string }> | undefined,
  displayColumn: { itemId: number; order: number }
): string => {
  const column = columns?.find((col) => col.itemId === displayColumn.itemId);
  const item = record.items.find((i) => i.columnKey === column?.columnKey);
  const value = item?.formattedValue ?? item?.rawValue;

  if (value === null || value === undefined || value === '') {
    return '';
  }

  if (Array.isArray(value)) {
    return value.map((v: unknown) => String(v)).join(', ');
  }

  if (typeof value === 'object' && value !== null && Object.keys(value).length > 0) {
    const obj = value as Record<string, unknown>;
    const stringProps = Object.values(obj).filter((v): v is string => typeof v === 'string' && v.trim() !== '');
    return stringProps.length > 0 ? stringProps[0] : '';
  }

  return String(value);
};

/**
 * Extracts and formats the value from a row data object for display.
 * Used in table contexts where data is already transformed.
 */
export const getDisplayColumnValueFromRow = (
  rowData: Record<string, unknown>,
  columns: Array<{ itemId: number; columnKey: string; label: string }> | undefined,
  displayColumn: { itemId: number; order: number }
): string => {
  const column = columns?.find((col) => col.itemId === displayColumn.itemId);
  if (!column) return '';

  const columnKey = column.columnKey.replace('.', '__DOT__');
  const value = rowData[columnKey];

  if (value === null || value === undefined || value === '') {
    return '';
  }

  if (Array.isArray(value)) {
    return value.map((v) => String(v)).join(', ');
  }

  if (typeof value === 'object' && value !== null && Object.keys(value).length > 0) {
    const obj = value as Record<string, unknown>;
    const stringProps = Object.values(obj).filter((v): v is string => typeof v === 'string' && v.trim() !== '');
    return stringProps.length > 0 ? stringProps[0] : '';
  }

  return String(value);
};

/**
 * Computes display text from displayColumns configuration.
 * Returns formatted string with values separated by " | ".
 */
export const computeDisplayColumnsText = (
  recordData:
    | {
        data: Array<{ items: Array<{ columnKey: string; formattedValue?: unknown; rawValue?: unknown }> }>;
        columns: Array<{ itemId: number; columnKey: string; label: string }>;
      }
    | undefined,
  displayColumns: DisplayColumn[] | undefined
): string | null => {
  if (!displayColumns || displayColumns.length === 0 || !recordData?.data || recordData.data.length === 0) {
    return null;
  }

  const record = recordData.data[0];
  const columns = recordData.columns;

  const values = displayColumns
    .slice()
    .sort((a, b) => a.order - b.order)
    .map((displayColumn) => getDisplayColumnValue(record, columns, displayColumn))
    .filter((v) => v !== '');

  return values.length > 0 ? values.join(' | ') : null;
};

/**
 * Computes display text from displayColumns configuration for table row data.
 * Returns formatted string with values separated by " | ".
 */
export const computeDisplayColumnsTextFromRow = (
  rowData: Record<string, unknown>,
  columns: Array<{ itemId: number; columnKey: string; label: string }> | undefined,
  displayColumns: DisplayColumn[] | undefined
): string | null => {
  if (!displayColumns || displayColumns.length === 0) {
    return null;
  }

  const values = displayColumns
    .slice()
    .sort((a, b) => a.order - b.order)
    .map((displayColumn) => getDisplayColumnValueFromRow(rowData, columns, displayColumn))
    .filter((v) => v !== '');

  return values.length > 0 ? values.join(' | ') : null;
};
